import { useEffect, useState, useCallback, useMemo } from 'react'
import { MiniTopic } from '@deepguide-ai/types'
import { getDataResolver, DataResolverOptions } from '../lib/data-resolver'

export interface UseExtensionDataOptions extends DataResolverOptions {
  /**
   * Whether to automatically refetch when options change
   */
  autoRefetch?: boolean
  /**
   * Whether to enable the hook (useful for conditional data fetching)
   */
  enabled?: boolean
}

export interface UseExtensionDataResult {
  /**
   * The resolved topic data
   */
  data: MiniTopic[]
  /**
   * Whether the hook is currently loading data
   */
  isLoading: boolean
  /**
   * Any error that occurred during data resolution
   */
  error: Error | null
  /**
   * Whether the data is from cache (legacy - always false now)
   */
  isFromCache: boolean
  /**
   * Function to manually refetch data
   */
  refetch: () => Promise<void>
}

/**
 * Hook for resolving extension data from multiple sources using Jotai state
 */
export function useExtensionData(options: UseExtensionDataOptions = {}): UseExtensionDataResult {
  const [data, setData] = useState<MiniTopic[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [isFromCache] = useState(false) // Always false since we don't cache anymore

  const {
    autoRefetch = true,
    enabled = true,
    ...resolverOptions
  } = options

  // Memoize the resolver options to prevent unnecessary re-renders
  const memoizedOptions = useMemo(() => resolverOptions, [
    resolverOptions.topicIds?.join(','),
    resolverOptions.selectedItems?.join(','),
    resolverOptions.customOrder?.join(','),
    resolverOptions.includeFallback,
    resolverOptions.fallbackData?.length,
  ])

  const resolver = getDataResolver()

  // Function to resolve data
  const resolveData = useCallback(async () => {
    if (!enabled) return

    setIsLoading(true)
    setError(null)

    try {
      const resolvedData = await resolver.resolve(memoizedOptions)
      setData(resolvedData)
    } catch (err) {
      const errorMessage = err instanceof Error ? err : new Error('Unknown error occurred')
      setError(errorMessage)
      console.error('useExtensionData: Failed to resolve data:', errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [resolver, memoizedOptions, enabled])

  // Effect to resolve data when options change
  useEffect(() => {
    if (autoRefetch && enabled) {
      resolveData()
    }
  }, [resolveData, autoRefetch, enabled])

  // Manual refetch function
  const refetch = useCallback(async () => {
    await resolveData()
  }, [resolveData])

  return {
    data,
    isLoading,
    error,
    isFromCache,
    refetch,
  }
}

/**
 * Hook to get the current workspace from the data resolver
 */
export function useWorkspaceFromResolver() {
  const resolver = getDataResolver()
  return resolver.getWorkspace()
}

/**
 * Hook specifically for folder contents data
 * Gets the direct children (subtopics) of a parent topic
 */
export function useFolderContentsData(options: {
  /**
   * The parent topic ID to get direct children from
   */
  selectedTopicId?: string
  /**
   * Array of topic IDs to display (filters direct children)
   */
  selectedItems?: string[]
  /**
   * Custom order for topic IDs
   */
  customOrder?: string[]
  /**
   * Serialized topic data to use as fallback
   */
  fallbackData?: MiniTopic[]
  /**
   * Whether to include fallback data
   */
  includeFallback?: boolean
  /**
   * Whether to enable the hook
   */
  enabled?: boolean
}) {
  const {
    selectedTopicId,
    selectedItems,
    customOrder,
    fallbackData,
    includeFallback = true,
    enabled = true,
  } = options

  // Use the extension data hook with the parent topic ID
  // The data resolver will find all direct children of this topic
  const result = useExtensionData({
    topicIds: selectedTopicId ? [selectedTopicId] : undefined,
    selectedItems,
    customOrder,
    fallbackData,
    includeFallback,
    enabled,
  })

  // Add debugging for folder contents specifically
  const { data, isLoading, error } = result

  useEffect(() => {
    if (selectedTopicId && !isLoading) {
      console.log('📁 [useFolderContentsData] Results for parent:', selectedTopicId, {
        directChildrenCount: data.length,
        childrenTitles: data.map(t => t.title),
        isFromFallback: fallbackData && fallbackData.length > 0 && data === fallbackData
      })
    }
  }, [selectedTopicId, data, isLoading, fallbackData])

  return result
}

/**
 * Hook specifically for screenshot step data
 * Uses data resolver with proper separation of concerns
 */
export function useScreenshotStepData(options: {
  /**
   * The step ID to resolve
   */
  stepId?: string
  /**
   * The topic ID (optional, will use different resolution strategy if provided)
   */
  topicId?: string
  /**
   * Whether to enable the hook
   */
  enabled?: boolean
}) {
  const {
    stepId,
    topicId,
    enabled = true,
  } = options

  const [step, setStep] = useState<any | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const resolver = getDataResolver()

  // Function to resolve step data using the appropriate strategy
  const resolveStep = useCallback(async () => {
    if (!enabled || !stepId) {
      setStep(null)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      let resolvedStep: any = null
      
      if (topicId) {
        // Use topic-based resolution (fetches all steps for topic)
        resolvedStep = await resolver.resolveStep(topicId, stepId)
        // console.log('📷 [useScreenshotStepData] Topic-based resolution:', {
        //   stepId,
        //   topicId,
        //   stepFound: !!resolvedStep,
        // })
      } else {
        // Use direct step-by-ID resolution (most efficient for single step)
        resolvedStep = await resolver.resolveStepById(stepId)
        // console.log('📷 [useScreenshotStepData] Direct step-by-ID resolution:', {
        //   stepId,
        //   stepFound: !!resolvedStep,
        // })
      }
      
      setStep(resolvedStep)
    } catch (err) {
      const errorMessage = err instanceof Error ? err : new Error('Unknown error occurred')
      setError(errorMessage)
      console.error('useScreenshotStepData: Failed to resolve step:', errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [resolver, stepId, topicId, enabled])

  useEffect(() => {
    resolveStep()
  }, [resolveStep])

  return {
    step,
    isLoading,
    error,
    refetch: resolveStep,
  }
}

/**
 * Hook to initialize the data resolver with context
 */
export function useDataResolverContext(
  contextResolver?: () => MiniTopic[],
  stepContextResolver?: (topicId: string) => any[],
  stepByIdApiResolver?: (stepId: string) => Promise<any | null>,
  workspaceResolver?: () => { id: string; slug: string; title: string } | null
) {
  const resolver = getDataResolver()

  useEffect(() => {
    const config: any = {}
    if (contextResolver) {
      config.contextResolver = contextResolver
    }
    if (stepContextResolver) {
      config.stepContextResolver = stepContextResolver
    }
    if (stepByIdApiResolver) {
      config.stepByIdApiResolver = stepByIdApiResolver
    }
    if (workspaceResolver) {
      config.workspaceResolver = workspaceResolver
    }
    if (Object.keys(config).length > 0) {
      resolver.updateConfig(config)
    }
  }, [resolver, contextResolver, stepContextResolver, stepByIdApiResolver, workspaceResolver])

  return resolver
}